import { FC, memo, useCallback, useRef, useState } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { MFA_CODE_LENGTH } from 'commons/constants/common'
import { Dialog, DialogFace } from 'ui/components/Dialog'
import { FF, Validators, handleFormSubmissionError } from 'ui/components/Form'
import { EmailInput, SecuredInput, USPhoneInput } from 'ui/components/InputV2'
import { Select } from 'ui/components/Select'
import { Col, FormRow } from 'ui/themes/globalStyles'
import { QR, RetryLink } from '~auth/components'

import * as SC from '../../../styles'
import { Footer } from './Footer'

export interface CreateDeviceDialogProps {
  onClose: () => void
  onCreateDevice: (value: { type: string; name: string }) => Promise<any>
  onResetChallenge: () => void
  onRetryChallenge: () => void
  onSendCode: (code: string) => Promise<any>
  challengeStartedAt: number
  totpUrl?: string
}

export type CreateDeviceFormValue = {
  type: string
  name: string
  code: string
}

const INITIAL_FORM_VALUE: CreateDeviceFormValue = {
  type: 'sms',
  name: '',
  code: ''
}

const postfix: { [key: string]: string } = {
  totp: ' shown in your MFA application',
  email: ' sent to your email',
  sms: ' sent you by SMS'
}

const deviceTypeOptions = [
  { label: 'Sms', value: 'sms' },
  { label: 'E-mail', value: 'email' },
  { label: 'Application', value: 'totp' }
]

export const CreateDeviceDialog: FC<CreateDeviceDialogProps> = memo((props) => {
  const { onClose, onCreateDevice, onResetChallenge, onRetryChallenge, onSendCode, challengeStartedAt, totpUrl } = props

  // Use refs to avoid stale closures in listener callbacks
  const onResetChallengeRef = useRef(onResetChallenge)
  const prevTypeRef = useRef(INITIAL_FORM_VALUE.type)
  const [challengeClearedAt, setChallengeClearedAt] = useState(0)

  // Keep refs in sync with props
  onResetChallengeRef.current = onResetChallenge
  const isChallengeActive = !!challengeStartedAt && challengeStartedAt > challengeClearedAt

  const form = useForm({
    defaultValues: INITIAL_FORM_VALUE,
    onSubmit: async ({ value }) => {
      try {
        await onCreateDevice(value)
      } catch (error) {
        handleFormSubmissionError(error, form)
      }
    }
  })

  const formValues = useStore(form.store, (s) => s.values)
  const isSms = formValues.type === 'sms'

  const onCloseWrapper = useCallback(() => {
    form.reset()
    onClose()
  }, [onClose, form])

  const sendCode = useCallback(
    (code: string) => {
      onSendCode(code)
        .then(() => {
          onCloseWrapper()
        })
        .catch((error) => {
          form.setFieldMeta('code', (meta) => ({
            ...meta,
            errors: [],
            errorMap: { onSubmit: error.message }
          }))
        })
    },
    [onSendCode, onCloseWrapper, form]
  )

  // Ref for sendCode to avoid stale closures
  const sendCodeRef = useRef(sendCode)
  sendCodeRef.current = sendCode

  // Handler for type field changes - reset form with new type, clear name/code
  const onTypeChange = useCallback(
    ({ value }: { value: string }) => {
      if (value === prevTypeRef.current) {
        return
      }
      prevTypeRef.current = value
      form.reset({ type: value, name: '', code: '' })
      if (isChallengeActive) {
        setChallengeClearedAt(Date.now())
        onResetChallengeRef.current()
      }
    },
    [form, isChallengeActive]
  )

  // Handler for name field changes - if challenge active, reset challenge
  const onNameChange = useCallback(() => {
    if (isChallengeActive) {
      form.resetField('code')
      setChallengeClearedAt(Date.now())
      onResetChallengeRef.current()
    }
  }, [form, isChallengeActive])

  // Handler for code field changes - auto-submit when code is complete
  const onCodeChange = useCallback(({ value }: { value: string }) => {
    if (value.length === MFA_CODE_LENGTH) {
      sendCodeRef.current(value)
    }
  }, [])

  return (
    <Dialog
      id="create-device-dialog"
      title="Add New MFA Device"
      face={DialogFace.PRIMARY}
      onClose={onCloseWrapper}
      stickyFooter={
        isChallengeActive ? undefined : (
          <form.Subscribe
            children={({ isSubmitting }) => (
              <>
                <Footer onCancel={onClose} onSubmit={form.handleSubmit} isSubmitting={isSubmitting} />
              </>
            )}
          />
        )
      }
    >
      <SC.Box>
        <FormRow>
          <Col>
            <form.Field
              name="type"
              listeners={{ onChange: onTypeChange }}
              children={(typeField) => (
                <FF
                  field={typeField}
                  label="Device Type"
                  input={(inputProps) => <Select data={deviceTypeOptions} {...inputProps} />}
                />
              )}
            />
          </Col>
        </FormRow>
        {formValues.type !== 'totp' && (
          <FormRow>
            <Col>
              <form.Field
                name="name"
                validators={{
                  onSubmit: ({ value }) => Validators.required.string(value)
                }}
                listeners={{ onChange: onNameChange }}
                children={(nameField) => (
                  <FF
                    required
                    field={nameField}
                    label={isSms ? 'Phone Number' : 'E-mail'}
                    input={(inputProps) => (isSms ? <USPhoneInput {...inputProps} /> : <EmailInput {...inputProps} />)}
                  />
                )}
              />
            </Col>
          </FormRow>
        )}
        {!!isChallengeActive && (
          <FormRow>
            <Col>
              <SC.SentHeader>Security code was {postfix[formValues.type]}</SC.SentHeader>
              <form.Field
                name="code"
                validators={{
                  onSubmit: ({ value }) => Validators.required.string(value)
                }}
                listeners={{ onChange: onCodeChange }}
                children={(codeField) => (
                  <FF
                    required
                    field={codeField}
                    label="Enter Security Code"
                    input={(inputProps) => (
                      <SecuredInput placeholder="• • • • • •" maxLength={6} data-lpignore={true} {...inputProps} />
                    )}
                  />
                )}
              />
              {totpUrl ? (
                <QR url={totpUrl} />
              ) : (
                <RetryLink dark startedAt={challengeStartedAt} onRetry={onRetryChallenge} />
              )}
            </Col>
          </FormRow>
        )}
      </SC.Box>
    </Dialog>
  )
})
